import axios from "axios";
import { notification } from "antd";
import { encodeString } from "../utils/helpers";
import {
  loginUserRequest,
  loginUserSuccess,
  loginUserFailed,
  getUserRequest,
  getUserSuccess,
  getUserFailed,
  registerUserRequest,
  registerUserSuccess,
  registerUserFailed,
  forgotPasswordRequest,
  forgotPasswordSuccess,
  forgotPasswordFailed,
  logoutUser,
} from "../reducers/userSlice";

export { activateFirstModal } from "../reducers/userSlice";

export function getErrorMessage(response) {
  if (!response) return "Unknown error";

  if (response.error && typeof response.error === "object") {
    const errors = response.error;
    for (const key in errors) {
      if (Array.isArray(errors[key])) return errors[key].join(", ");
      if (typeof errors[key] === "string") return errors[key];
    }
  }

  if (response.message && typeof response.message === "string") {
    return response.message;
  }

  if (typeof response.error === "string") {
    return response.error;
  }

  return "Unknown error";
}

export const getUser = (loginToken) => (dispatch) => {
  dispatch(getUserRequest());
  return axios({
    url: "/api/get_user ",
    method: "GET",
    headers: {
      Authorization: `Bearer ${loginToken}`,
    },
  })
    .then(({ data }) => {
      const { flag } = data;
      if (flag) {
        const { data: response } = data;
        const { user_info } = response;

        let user = {
          ...user_info,
        };

        axios.defaults.headers.common["Authorization"] = `Bearer ${loginToken}`;
        delete user.api_token;

        return dispatch(
          getUserSuccess({
            user,
            loginToken,
          }),
        );
      } else {
        const { message } = data;
        notification.error({
          message: "GET USER",
          description: getErrorMessage(message),
        });
        return dispatch(getUserFailed());
      }
    })
    .catch(() => {
      localStorage.setItem("expire", true);
      return dispatch(getUserFailed());
    });
};

export const logout = () => (dispatch) => {
  const authToken = localStorage.getItem("loginToken");
  return axios({
    url: "/api/logout_api",
    method: "GET",
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  }).then(() => {
    dispatch(logoutUser());
    delete axios.defaults.headers.common.Authorization;
    localStorage.removeItem("loginToken");
    window.location.href = "/auth/login";
  })
  .catch(() => {
    dispatch(logoutUser());
    delete axios.defaults.headers.common.Authorization;
    localStorage.removeItem("loginToken");
    window.location.href = "/auth/login";
  });
};

/**
 * Read-only session check (IC-84 / IC-65).
 * GET /api/session/status with JSON body { api_token }.
 * On invalid/401: clear client auth state only (no logout_api).
 */
export const checkSessionStatus = () => (dispatch) => {
  const api_token = localStorage.getItem("loginToken");
  if (!api_token) {
    return Promise.resolve({ valid: false, skipped: true });
  }

  return axios({
    url: "/api/session/status",
    method: "GET",
    data: { api_token },
    headers: {
      "Content-Type": "application/json",
    },
  })
    .then(({ data }) => {
      if (data?.valid === false) {
        dispatch(logoutUser());
        delete axios.defaults.headers.common.Authorization;
        return { valid: false };
      }
      return {
        valid: true,
        expiresAt: data?.expiresAt ?? null,
        secondsRemaining: data?.secondsRemaining ?? null,
        refreshable: data?.refreshable,
        refreshableUntil: data?.refreshableUntil ?? null,
      };
    })
    .catch((error) => {
      if (error?.response?.status === 401) {
        dispatch(logoutUser());
        delete axios.defaults.headers.common.Authorization;
        return { valid: false };
      }
      return Promise.reject(error);
    });
};

export const forgotPassword = (user, history) => (dispatch) => {
  dispatch(forgotPasswordRequest());

  return axios({
    url: "/api/forgot_password_by_email_by_link",
    method: "POST",
    data: {
      email: user?.userNameOrEmail,
    },
  })
    .then(() => {
      dispatch(forgotPasswordSuccess());
      notification.success({
        message: "FORGOT",
        description: "FORGOT PASSWORD LINK IS SEND TO YOUR EMAIL ADDRESS",
      });

      history.push("/auth/login");
    })
    .catch((error) => {
      notification.error({
        message: "FORGOT_PASSWORD_FAILED",
        description: error.message,
      });

      return dispatch(forgotPasswordFailed());
    });
};

export const registerUser =
  ({ fname, lname, email, password, phoneNumber }) =>
  (dispatch) => {
    dispatch(registerUserRequest());
    return axios({
      url: "/api/registerAsUser",
      method: "POST",
      data: {
        first_name: fname,
        last_name: lname,
        email,
        password,
        phone: phoneNumber,
      },
    })
      .then(({ data }) => {
        const { flag } = data;
        if (flag) {
          const { message, data: response } = data;
          const { user_info } = response;

          const loginToken = user_info.api_token;
          axios.defaults.headers.common["Authorization"] =
            `Bearer ${loginToken}`;
          localStorage.setItem("loginToken", loginToken);

          notification.success({
            message: "SIGNUP",
            description: message.message,
          });
          let user = {
            ...user_info,
          };
          delete user.api_token;
          dispatch(
            registerUserSuccess({
              ...user,
              loginToken,
              loading: false,
            }),
          );
          return "success";
        } else {
          const { message } = data;
          if (message && message.error.email) {
            notification.error({
              message: "SIGNUP",
              description: message.error.email,
            });
          } else if (message && message.error.password) {
            notification.error({
              message: "SIGNUP",
              description: message.error.password,
            });
          } else if (message && message.error.first_name) {
            notification.error({
              message: "SIGNUP",
              description: message.error.first_name,
            });
          } else if (message && message.error.last_name) {
            notification.error({
              message: "SIGNUP",
              description: message.error.last_name,
            });
          } else {
            notification.error({
              message: "SIGNUP",
              description: "SIGNUP NOT SUCCESSFULL !",
            });
          }

          dispatch(registerUserFailed());
          return "failed";
        }
      })
      .catch((error) => {
        notification.error({
          message: "SIGNUP",
          description: error.message,
        });

        dispatch(registerUserFailed());
        return "failed";
      });
  };

export const loginUser =
  ({ userNameOrEmail, password, first_name, last_name, id_token, provider }) =>
  (dispatch) => {
    dispatch(loginUserRequest());
    return axios({
      url: "/api/login",
      method: "POST",
      data: {
        id_token,
        provider,
        email: userNameOrEmail,
        password,
        first_name,
        last_name,
      },
    })
      .then(({ data }) => {
        const { flag } = data;
        if (flag) {
          const { data: response } = data;
          const { user_info } = response;

          const loginToken = user_info.api_token;
          axios.defaults.headers.common["Authorization"] =
            `Bearer ${loginToken}`;
          let user = {
            ...user_info,
          };
          delete user.api_token;
          dispatch(
            loginUserSuccess({
              ...user,
              loginToken,
              loading: false,
              isLogin: false,
            }),
          );
          let encryptedPassword = encodeString(loginToken, password);
          localStorage.setItem("loginToken", loginToken);
          window.location = `${process.env.REACT_APP_BACKEND_BASE_URL}/loginWithToken?token=${encryptedPassword}`;
        } else {
          const { message } = data;
          notification.error({
            message: "LOGIN",
            description: getErrorMessage(message),
          });
          return dispatch(loginUserFailed());
        }
      })
      .catch((error) => {
        notification.error({
          message: "LOGIN",
          description: error.message,
        });

        return dispatch(loginUserFailed());
      });
  };

export const checkUserLoginStatusOnDashboard = (user) => (dispatch) => {
  dispatch({
    type: "GET_USER_LOGIN_STATUS_REQUEST",
    payload: { loading: true },
  });
  return axios({
    url: "/api/get_login_detail",
    method: "GET",
    params: {
      user_id: user.user?.id,
    },
    headers: {
      Authorization: `Bearer ${user.loginToken}`,
    },
  })
    .then(({ data }) => {
      const checking = data?.data?.user?.login_status;
      if (checking === "0" || checking === 0) {
        dispatch(logoutUser());
        return axios({
          url: "/api/logout_api",
          method: "GET",
          headers: {
            Authorization: `Bearer ${user.loginToken}`,
          },
        }).then(() => {
          dispatch(logoutUser());
          delete axios.defaults.headers.common.Authorization;
          localStorage.removeItem("loginToken");
        });
      }
      const { flag } = data;
      if (flag) {
        const { data: response } = data;
        let { login_status } = response.user;
        if (login_status) {
          dispatch(
            loginUserSuccess({
              ...user,
              loading: false,
              isLogin: true,
            }),
          );
        }
        return response;
      } else {
        const { message } = data;
        return dispatch(getUserFailed());
      }
    })
    .catch((error) => {
      notification.error({
        message: "GET USER",
        description: error?.message,
      });

      return dispatch(getUserFailed());
    });
};

export const checkedEmail = (email, state) => (dispatch) => {
  dispatch(registerUserRequest());
  return axios({
    url: "/api/emailVerifaction",
    method: "POST",
    data: {
      email,
    },
  })
    .then((res) => {
      const { message, flag } = res?.data;
      if (flag) {
        dispatch(registerUserSuccess());
        state();
      } else {
        dispatch(registerUserFailed());
        state(message && message?.error);
      }
    })
    .catch(() => {
      dispatch(registerUserFailed());
    });
};

export const signupOtpAction = (values, history) => (dispatch) => {
  dispatch(registerUserRequest());
  return axios({
    url: "/api/signupOtpEmail",
    method: "POST",
    data: {
      email: values?.email,
    },
  })
    .then((res) => {
      dispatch(registerUserSuccess());
      const { message, flag } = res?.data;
      if (flag) {
        history.push({
          pathname: "/auth/verification",
          state: {
            signup: values,
            bussiness: false,
          },
        });
      } else {
        dispatch(registerUserFailed()); // ✅ STOP loader
        notification.error({
          message: "Error",
          description: message && message.error,
        });
      }
    })
    .catch((error) => {
      dispatch(registerUserFailed()); // ✅ STOP loader
      notification.error({
        message: "GET USER",
        description: error?.message,
      });
    });
};

export const verificationAction = (values, history) => (dispatch) => {
  dispatch(registerUserRequest());
  return axios({
    url: "/api/otpEmail",
    method: "POST",
    data: {
      email: values?.email,
    },
  })
    .then((res) => {
      dispatch(registerUserSuccess());
      history.push({
        pathname: "/auth/verification",
        state: {
          signup: values,
          bussiness: false,
        },
      });
    })
    .catch((error) => {
      dispatch(registerUserFailed());
      notification.error({
        message: "GET USER",
        description: error?.message,
      });
    });
};

export const verificationActionPhone = (values) => (dispatch) => {
  dispatch(registerUserRequest());
  return axios({
    url: "/api/otpPhone",
    method: "POST",
    data: {
      phone: values?.phoneNumber,
    },
  })
    .then((res) => {
      if (res.data.flag) {
        dispatch(registerUserSuccess());
        notification.success({
          message: "OTP SENT",
          description: res?.data?.message?.message,
        });
      } else {
        dispatch(registerUserFailed());
        notification.error({
          message: "OTP DID NOT SENT",
          description: res?.data?.message.error.split(":")[1],
        });
      }
    })
    .catch((error) => {
      dispatch(registerUserFailed());
      notification.error({
        message: "OTP DID NOT SENT",
        description: error?.message,
      });
    });
};

export const verificationActionEmail = (values) => (dispatch) => {
  dispatch(registerUserRequest());
  return axios({
    url: "/api/otpEmail",
    method: "POST",
    data: {
      email: values?.email,
    },
  })
    .then((res) => {
      if (res.data.flag) {
        dispatch(registerUserSuccess());
        notification.success({
          message: "OTP SENT",
          description: res?.data?.message?.message,
        });
      } else {
        dispatch(registerUserFailed());
        notification.error({
          message: "OTP DID NOT SENT",
          description: res?.message.error,
        });
      }
    })
    .catch((error) => {
      dispatch(registerUserFailed()); 
      notification.error({
        message: "OTP DID NOT SENT",
        description: error?.message,
      });
    });
};

export const verificationCodeAction =
  (values, history, code, bit, postCode, isEmail) =>
  async (dispatch) => {
    try {
      dispatch(registerUserRequest());

      // ---------------------------------------------------------------------
      // OTP Verification
      // ---------------------------------------------------------------------

      const otpResponse = await axios({
        url: isEmail
          ? "/api/otp_verification"
          : "/api/otp_verification_by_phone",
        method: "POST",
        data: isEmail
          ? {
              email: values.email,
              otp: code,
            }
          : {
              phone: values.phoneNumber,
              otp: code,
            },
      });

      if (otpResponse?.data?.flag === 0) {
        const errorMessage =
          otpResponse?.data?.message?.error || "OTP verification failed";

        notification.error({
          message: "ERROR",
          description: errorMessage,
        });

        dispatch(
          registerUserFailed(),
        );

        return;
      }

      // ---------------------------------------------------------------------
      // User Signup
      // ---------------------------------------------------------------------

      if (history?.location?.state?.bussiness === false) {
        if (bit !== "signUp") {
          const errorMessage =
            otpResponse?.data?.message?.error?.otp?.[0] ||
            "Invalid OTP";

          notification.error({
            message: "SIGNUP",
            description: errorMessage,
          });

          dispatch(
            registerUserFailed(),
          );

          return;
        }

        const signupResponse = await axios({
          url: "/api/registerAsUser",
          method: "POST",
          data: {
            first_name: values?.fname,
            last_name: values?.lname,
            email: values?.email,
            password: values?.password,
            phone: values?.phoneNumber,
            otp: code,
          },
        });

        const responseData = signupResponse?.data;

        if (!responseData?.flag) {
          const message = responseData?.message;
          const errorMessage =
            message?.error?.email ||
            message?.error?.password ||
            message?.error?.first_name ||
            message?.error?.last_name ||
            "SIGNUP NOT SUCCESSFUL!";

          notification.error({
            message: "SIGNUP",
            description: errorMessage,
          });

          dispatch(
            registerUserFailed(),
          );

          return;
        }

        const {
          message,
          data: response,
        } = responseData;

        const { user_info } = response;

        const loginToken = user_info?.api_token;

        axios.defaults.headers.common.Authorization =
          `Bearer ${loginToken}`;

        localStorage.setItem("loginToken", loginToken);

        const encryptedPassword = encodeString(
          loginToken,
          values?.password,
        );

        notification.success({
          message: "SIGNUP",
          description: message?.message,
        });

        const user = {
          ...user_info,
        };

        delete user.api_token;

        dispatch(
          registerUserSuccess(),
        );

        window.location = `${process.env.REACT_APP_BACKEND_BASE_URL}/loginWithToken?token=${encryptedPassword}`;

        return;
      }

      // ---------------------------------------------------------------------
      // Company Registration
      // ---------------------------------------------------------------------

      if (history?.location?.state?.bussiness) {
        dispatch({
          type: "REGISTER_COMPANY_REQUEST",
          payload: { loading: true },
        });

        const companyResponse = await axios({
          url: "/api/registerAsCompany",
          method: "POST",
          data: {
            ...values,
            postCode: postCode?.postcode,
            otp: code,
          },
        });

        const responseData = companyResponse?.data;

        if (!responseData?.flag) {
          const message = responseData?.message;

          const errorMessage =
            message?.error?.email ||
            message?.error ||
            "Company registration failed";

          notification.error({
            message: "REGISTER COMPANY",
            description: errorMessage,
          });

          dispatch(
            registerUserFailed(),
          );

          dispatch({
            type: "REGISTER_COMPANY_FAILED",
            payload: { loading: false },
          });

          return "failed";
        }

        notification.success({
          message: "REGISTER COMPANY",
          description: responseData?.message?.message,
        });

        dispatch(registerUserSuccess());

        dispatch({
          type: "REGISTER_COMPANY_SUCCESS",
          payload: {
            loading: false,
            status: true,
          },
        });

        history.push("/auth/login");

        return ;
      }

      dispatch(registerUserSuccess());

      return;
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong";

      notification.error({
        message: "SIGNUP",
        description: errorMessage,
      });

      dispatch(
        registerUserFailed(),
      );

      return;
    }
  };